import React, { useState } from 'react';

const ACCENT = '#4A7C59';

export const ResetPassword: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const isInvite = params.get('invite') === '1';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const title = isInvite ? 'Set up your account' : 'Set new password';
  const buttonLabel = isInvite ? 'Set up account' : 'Set new password';

  if (!token) {
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <div className="w-full max-w-sm p-8 text-center">
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            Invalid link. This link is missing required information.
          </div>
          <a href="/production/login" className="text-sm font-medium" style={{ color: ACCENT }}>
            Back to login
          </a>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/production/auth?action=reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setSuccess(true);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isExpiredError = /expired/i.test(error);

  return (
    <div className="admin-shell min-h-screen flex items-center justify-center bg-[#FAFAF8]">
      <div className="w-full max-w-sm p-8">
        {/* Back to login */}
        <a
          href="/production/login"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] hover:text-[#1A1A1A] mb-8"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to login
        </a>

        <div className="mb-8">
          <div
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg mb-4"
            style={{ backgroundColor: ACCENT }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">{title}</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {isInvite
              ? 'Choose a password to activate your Lot 20 Production account.'
              : 'Enter your new password below.'}
          </p>
        </div>

        {success ? (
          <div>
            <div className="mb-6 p-4 rounded-lg" style={{ background: `${ACCENT}1A`, border: `1px solid ${ACCENT}33` }}>
              <p className="text-sm font-medium" style={{ color: '#2D5C3F' }}>Password set successfully!</p>
              <p className="text-sm mt-1" style={{ color: '#2D5C3F' }}>
                You can now sign in with your new password.
              </p>
            </div>
            <a
              href="/production/login"
              className="block w-full py-3 text-center text-white rounded-lg font-medium"
              style={{ backgroundColor: ACCENT }}
            >
              Go to login
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
                {isExpiredError && (
                  <div className="mt-2">
                    <a href="/production/forgot-password" className="font-medium underline" style={{ color: ACCENT }}>
                      Request a new link
                    </a>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
                New password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
                className="w-full px-4 py-3 border border-[#E8E6E2] rounded-lg bg-white text-[#1A1A1A] placeholder-[#A3A3A3] focus:outline-none"
                onFocus={e => (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`)}
                onBlur={e => (e.target.style.boxShadow = '')}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[#6B6B6B] uppercase tracking-wider mb-1.5">
                Confirm password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                required
                className="w-full px-4 py-3 border border-[#E8E6E2] rounded-lg bg-white text-[#1A1A1A] placeholder-[#A3A3A3] focus:outline-none"
                onFocus={e => (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`)}
                onBlur={e => (e.target.style.boxShadow = '')}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full py-3 text-white rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: ACCENT }}
            >
              {loading ? 'Saving...' : buttonLabel}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
